import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import { useId } from "../EditActivityContainer";
import type { ActivityUpdatePayload } from "@features/activities/activityTypes";
import { useUpdateActivityMutation } from "@hooks/index";
import { validatePositiveNumber } from "@pages/activityBuilder/modules/moduleUtils";
import { selectCurrentProject } from "@features/project/projectSlice";
import { useAppSelector } from "@app/hooks";
import { initValues } from "../utils";
import { createActivityInitialValues, createActivityValidationSchema, toActivityPayloadFields } from "../activityForm";
import { useFirebaseHook, EventTypes } from "@utils/firebaseUtils";
import { useHandleActivityState } from "@pages/activityBuilder/hooks/useHandleActivityState";
import useErrorMessage from "@utils/useErrorMessage";

export const useEditActivity = ({ setIsSuccessModalOpen }: { setIsSuccessModalOpen: (isOpen: boolean) => void }) => {
  const { id } = useId()
  const { project } = useAppSelector(selectCurrentProject);
  const { activity, isFetching, isError, error } = useHandleActivityState();
  const { fireEvent } = useFirebaseHook();
  const [initialvalues, setInitialvalues] =
    useState<ActivityUpdatePayload>(initValues);

  const [
    updateActivity,
    {
      isLoading: isUpdateLoading,
      isError: isUpdateError,
      error: updateError,
      isSuccess: isUpdateSuccess,
    },
  ] = useUpdateActivityMutation();

  const { errorMsg: blockingError } = useErrorMessage({ isError, error });
  const { errorMsg: updateErrorMessage } = useErrorMessage({
    isError: isUpdateError,
    error: updateError,
  });

  const validationSchema = useMemo(
    () =>
      project
        ? createActivityValidationSchema(project).shape({ id: validatePositiveNumber.required() })
        : Yup.object(),
    [project],
  )

  useEffect(() => {
    // Guard against stale Redux data from a previously viewed activity
    if (!activity || activity.id !== id || !project) return;
    setInitialvalues(createActivityInitialValues(project, { id: id ?? 0, activity }));
  }, [activity, id, project]);


  const handleSubmit = async (values: ActivityUpdatePayload) => {
    try {
      const payload = {
        ...values,
        activity: {
          ...values.activity,
          ...toActivityPayloadFields(values.activity, project),
        }
      }
      await updateActivity(payload).unwrap().then(() => fireEvent(EventTypes.edit_activity))
      setIsSuccessModalOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  return {
    activity,
    initialvalues,
    validationSchema,
    handleSubmit,
    isUpdateLoading,
    isUpdateError,
    updateErrorMessage,
    blockingError,
    id,
    isUpdateSuccess,
    isFetching,
  }
}